import React, { useEffect, useState } from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import { warbandMemberImageService } from '../../services/warbandMemberImageService';
import ImageUploader from './ImageUploader';
import { DriveImageGalleryModal } from '../modals/DriveImageGalleryModal';
import { useAuthStore } from '../../stores/authStore';
import { syncService } from '../../services/syncService';

interface MemberImageProps {
  warbandId: string;
  memberId: string;
  editMode: boolean;
  isTemporary?: boolean;
  accessToken: string | null;
  minified?: boolean;
}

export const MemberImage: React.FC<MemberImageProps> = ({
  warbandId,
  memberId,
  editMode,
  isTemporary,
  accessToken,
  minified
}) => {
  const [imageVersion, setImageVersion] = useState(0);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [isPendingSync, setIsPendingSync] = useState(false);
  const syncStatus = useAuthStore((state) => state.syncStatus);
  const setSyncStatus = useAuthStore((state) => state.setSyncStatus);
  const setPendingImageConflicts = useAuthStore((state) => state.setPendingImageConflicts);

  // Refetch only when a sync completes — not on every status transition
  useEffect(() => {
    if (syncStatus === 'success') setImageVersion((v) => v + 1);
  }, [syncStatus]);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setIsPendingSync(warbandMemberImageService.hasPendingImage(warbandId, memberId));
    warbandMemberImageService.fetchImage(warbandId, memberId)
      .then(blob => {
        if (cancelled) return;
        if (blob) {
          blobUrl = URL.createObjectURL(blob);
          setImageBlobUrl(blobUrl);
        } else {
          setImageBlobUrl(null);
        }
      })
      .catch(() => { if (!cancelled) setImageBlobUrl(null); });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [warbandId, memberId, imageVersion, accessToken]);

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    try {
      await warbandMemberImageService.uploadImage(warbandId, memberId, file);
      setIsPendingSync(true);
      setImageVersion(v => v + 1);
    } catch {
      setUploadError('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDriveImageSelect = async (driveFileId: string, token: string) => {
    setIsUploading(true);
    setUploadError(null);
    try {
      await warbandMemberImageService.assignDriveImage(warbandId, memberId, driveFileId, token);
      setImageVersion(v => v + 1);
      setGalleryOpen(false);
      setSyncStatus('syncing');
      const result = await syncService.syncWarband(warbandId, token);
      if (result.imageConflicts.length > 0) {
        setPendingImageConflicts(result.imageConflicts);
        setSyncStatus('idle');
      } else {
        setPendingImageConflicts([]);
        setIsPendingSync(false);
        setSyncStatus('success');
      }
    } catch {
      setUploadError('Failed to fetch image from Drive');
    } finally {
      setIsUploading(false);
    }
  };

  const dimension = minified ? 64 : 192;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 1, sm: 0 },
        mr: { xs: 0, sm: 2 },
      }}
    >
      <Avatar
        variant="rounded"
        src={imageBlobUrl ?? undefined}
        alt="Member avatar"
        sx={{ width: dimension, height: dimension, bgcolor: 'grey.200', fontSize: minified ? 16 : 32 }}
        imgProps={{ style: { objectFit: 'cover' } }}
      >
        ?
      </Avatar>
      {(!minified && (editMode || isTemporary)) && (
        <Box sx={{ mt: 1, mb: 0, position: 'relative', top: -20 }}>
          {accessToken ? (
            <Button
              size="small"
              variant="contained"
              sx={{ minWidth: 0, px: 1, fontSize: 12 }}
              disabled={isUploading}
              onClick={() => setGalleryOpen(true)}
            >
              {isUploading ? 'Uploading…' : 'Select Image'}
            </Button>
          ) : (
            <ImageUploader
              onImageResized={handleImageUpload}
              isUploading={isUploading}
            />
          )}
        </Box>
      )}
      {uploadError && (
        <Typography color="error" variant="caption">{uploadError}</Typography>
      )}
      {isPendingSync && (
        <Typography color="warning.main" variant="caption">Pending sync</Typography>
      )}
      {galleryOpen && accessToken && (
        <DriveImageGalleryModal
          open={galleryOpen}
          accessToken={accessToken}
          onSelect={handleDriveImageSelect}
          onClose={() => setGalleryOpen(false)}
        />
      )}
    </Box>
  );
};
